import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { addDays, differenceInMinutes, format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { formatInTimeZone } from 'date-fns-tz';
import {
  Calendar as CalendarIcon,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Clock,
  Filter,
  Phone,
  Plus,
  Scissors,
  User,
  XCircle,
} from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import type {
  AgendaAppointment,
  AgendaDayResponse,
  AppointmentStatus,
} from '@agenda-facil/contracts';
import { Button, Modal, Skeleton, StatusPill, Surface, Tabs, Toast } from '@agenda-facil/ui';
import { ErrorState } from '../../components/query-feedback';
import { AgendaTimeline } from '../../features/agenda/agenda-timeline';
import {
  appointmentStatusLabel as statusLabel,
  appointmentStatusTone as statusTone,
} from '../../features/agenda/appointment-status';
import { api, ApiClientError } from '../../lib/api';

const currency = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

interface Feedback {
  title: string;
  description?: string;
  type: 'success' | 'danger' | 'info';
}

export function AgendaPage() {
  const queryClient = useQueryClient();
  const [selectedDate, setSelectedDate] = useState(() => new Date());
  const [viewMode, setViewMode] = useState('timeline');
  const [selectedAppointment, setSelectedAppointment] = useState<AgendaAppointment>();
  const [feedback, setFeedback] = useState<Feedback>();
  const date = format(selectedDate, 'yyyy-MM-dd');
  const agenda = useQuery({
    queryKey: ['appointments', date],
    queryFn: () => api.get<AgendaDayResponse>(`/appointments?date=${date}`),
  });
  const updateStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: AppointmentStatus }) =>
      api.put<{ id: string; status: AppointmentStatus }>(`/appointments/${id}/status`, { status }),
    onSuccess: async (appointment) => {
      setSelectedAppointment(undefined);
      setFeedback({
        type: 'success',
        title: `Agendamento atualizado para ${statusLabel[appointment.status].toLowerCase()}.`,
      });
      await queryClient.invalidateQueries({ queryKey: ['appointments', date] });
    },
    onError: (caught) =>
      setFeedback({
        type: 'danger',
        title: 'Não foi possível atualizar o agendamento.',
        description:
          caught instanceof ApiClientError ? caught.message : 'Tente novamente em instantes.',
      }),
  });

  const appointments = agenda.data?.appointments ?? [];
  const professionals = agenda.data?.professionals ?? [];
  const timezone = agenda.data?.shop.timezone ?? 'America/Sao_Paulo';
  const formattedDate = format(selectedDate, "EEEE, d 'de' MMMM", { locale: ptBR });

  return (
    <div className="space-y-6">
      {feedback && (
        <Toast
          type={feedback.type}
          title={feedback.title}
          description={feedback.description}
          onClose={() => setFeedback(undefined)}
          className="fixed right-4 top-4 z-50 max-w-md"
        />
      )}

      <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <span className="eyebrow">Agenda da barbearia</span>
          <h1 className="display mt-2 text-3xl font-extrabold capitalize text-[var(--foreground)] sm:text-4xl">
            {formattedDate}
          </h1>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Tabs
            items={[
              { id: 'timeline', label: 'Por profissional', icon: <CalendarIcon size={14} /> },
              {
                id: 'list',
                label: 'Lista',
                icon: <Filter size={14} />,
                count: appointments.length,
              },
            ]}
            activeId={viewMode}
            onChange={setViewMode}
          />
          <div className="flex items-center rounded-2xl border border-[var(--border)] bg-[var(--surface)] p-1 shadow-[var(--shadow-sm)]">
            <button
              type="button"
              onClick={() => setSelectedDate((current) => addDays(current, -1))}
              className="grid size-10 place-items-center rounded-xl text-[var(--foreground)] transition-colors hover:bg-[var(--surface-raised)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
              aria-label="Dia anterior"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              type="button"
              onClick={() => setSelectedDate(new Date())}
              className="min-h-10 rounded-xl px-3 text-xs font-black text-[var(--foreground)] transition-colors hover:bg-[var(--surface-raised)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
            >
              Hoje
            </button>
            <button
              type="button"
              onClick={() => setSelectedDate((current) => addDays(current, 1))}
              className="grid size-10 place-items-center rounded-xl text-[var(--foreground)] transition-colors hover:bg-[var(--surface-raised)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
              aria-label="Próximo dia"
            >
              <ChevronRight size={18} />
            </button>
          </div>
          {agenda.data?.shop.published ? (
            <Button asChild>
              <Link to={`/barbearia/${agenda.data.shop.slug}/agendar`}>
                <Plus size={16} /> Novo agendamento
              </Link>
            </Button>
          ) : (
            <Button disabled title="Publique a página da barbearia para receber agendamentos.">
              <Plus size={16} /> Novo agendamento
            </Button>
          )}
        </div>
      </div>

      {agenda.isLoading ? (
        <AgendaSkeleton />
      ) : agenda.isError || !agenda.data ? (
        <ErrorState
          message="Não foi possível carregar a agenda."
          retry={() => void agenda.refetch()}
        />
      ) : professionals.length === 0 ? (
        <AgendaEmpty
          title="Cadastre um profissional"
          message="A agenda será exibida depois que a equipe e seus horários forem configurados."
        />
      ) : viewMode === 'timeline' ? (
        <AgendaTimeline
          appointments={appointments}
          professionals={professionals}
          timezone={timezone}
          date={date}
          bookingPath={
            agenda.data.shop.published ? `/barbearia/${agenda.data.shop.slug}/agendar` : undefined
          }
          select={setSelectedAppointment}
        />
      ) : appointments.length === 0 ? (
        <AgendaEmpty
          title="Dia livre"
          message="Nenhum agendamento confirmado para esta data."
          bookingPath={
            agenda.data.shop.published ? `/barbearia/${agenda.data.shop.slug}/agendar` : undefined
          }
        />
      ) : (
        <AgendaList
          appointments={appointments}
          timezone={timezone}
          select={setSelectedAppointment}
        />
      )}

      {selectedAppointment && (
        <AppointmentDetails
          appointment={selectedAppointment}
          timezone={timezone}
          pending={updateStatus.isPending}
          close={() => setSelectedAppointment(undefined)}
          update={(status) => updateStatus.mutate({ id: selectedAppointment.id, status })}
        />
      )}
    </div>
  );
}

function AgendaList({
  appointments,
  timezone,
  select,
}: {
  appointments: AgendaAppointment[];
  timezone: string;
  select: (appointment: AgendaAppointment) => void;
}) {
  return (
    <Surface className="divide-y divide-[var(--border)] p-2 sm:p-4">
      {appointments.map((appointment) => (
        <button
          key={appointment.id}
          type="button"
          onClick={() => select(appointment)}
          className="flex w-full flex-col gap-3 rounded-2xl p-4 text-left transition-colors hover:bg-[var(--surface-raised)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)] sm:flex-row sm:items-center sm:justify-between"
        >
          <div className="flex items-center gap-4">
            <span className="grid size-12 shrink-0 place-items-center rounded-2xl bg-[var(--accent-soft)] text-sm font-black text-[var(--accent)]">
              {formatInTimeZone(appointment.startsAt, timezone, 'HH:mm')}
            </span>
            <div className="min-w-0">
              <p className="truncate text-sm font-extrabold">{appointment.customerName}</p>
              <p className="mt-0.5 truncate text-xs text-[var(--muted-foreground)]">
                {appointment.serviceName} · {appointment.barberName}
              </p>
            </div>
          </div>
          <div className="flex items-center justify-between gap-4 sm:justify-end">
            <span className="text-sm font-black">
              {currency.format(appointment.priceCents / 100)}
            </span>
            <StatusPill tone={statusTone[appointment.status]}>
              {statusLabel[appointment.status]}
            </StatusPill>
          </div>
        </button>
      ))}
    </Surface>
  );
}

function AppointmentDetails({
  appointment,
  timezone,
  pending,
  close,
  update,
}: {
  appointment: AgendaAppointment;
  timezone: string;
  pending: boolean;
  close: () => void;
  update: (status: AppointmentStatus) => void;
}) {
  const canUpdate = !['COMPLETED', 'CANCELED'].includes(appointment.status);
  return (
    <Modal
      isOpen
      onClose={close}
      title="Detalhes do agendamento"
      description={`${formatInTimeZone(appointment.startsAt, timezone, 'HH:mm')} · ${appointment.barberName}`}
      footer={
        canUpdate ? (
          <>
            <Button
              variant="ghost"
              size="sm"
              disabled={pending}
              onClick={() => update('CANCELED')}
              className="text-[var(--danger)] hover:bg-[var(--danger-soft)]"
            >
              <XCircle size={15} /> Cancelar
            </Button>
            <Button size="sm" disabled={pending} onClick={() => update('COMPLETED')}>
              <CheckCircle size={15} /> Concluir atendimento
            </Button>
          </>
        ) : undefined
      }
    >
      <div className="space-y-4">
        <div className="flex items-center gap-3 rounded-2xl border border-[var(--border)] bg-[var(--input)] p-3.5">
          <User size={20} className="text-[var(--accent)]" />
          <div className="min-w-0 flex-1">
            <p className="text-xs font-bold text-[var(--muted-foreground)]">Cliente</p>
            <p className="truncate text-sm font-extrabold">{appointment.customerName}</p>
            <p className="truncate text-xs text-[var(--muted-foreground)]">
              {appointment.customerEmail}
            </p>
          </div>
          <a
            href={`tel:${appointment.customerPhone}`}
            className="grid size-10 place-items-center rounded-xl bg-[var(--surface)] text-[var(--accent)] transition-colors hover:bg-[var(--accent-soft)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
            aria-label={`Ligar para ${appointment.customerName}`}
          >
            <Phone size={16} />
          </a>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Detail label="Serviço" icon={Scissors} value={appointment.serviceName} />
          <Detail
            label="Duração e valor"
            icon={Clock}
            value={`${differenceInMinutes(new Date(appointment.endsAt), new Date(appointment.startsAt))} min · ${currency.format(appointment.priceCents / 100)}`}
          />
        </div>
        <div className="flex items-center justify-between rounded-2xl bg-[var(--surface-raised)] px-4 py-3">
          <span className="text-xs font-bold text-[var(--muted-foreground)]">Status atual</span>
          <StatusPill tone={statusTone[appointment.status]}>
            {statusLabel[appointment.status]}
          </StatusPill>
        </div>
      </div>
    </Modal>
  );
}

function Detail({
  label,
  icon: Icon,
  value,
}: {
  label: string;
  icon: typeof Clock;
  value: string;
}) {
  return (
    <div className="rounded-2xl border border-[var(--border)] bg-[var(--input)] p-3.5">
      <Icon size={18} className="mb-1 text-[var(--accent)]" />
      <p className="text-xs font-bold text-[var(--muted-foreground)]">{label}</p>
      <p className="mt-0.5 text-xs font-extrabold">{value}</p>
    </div>
  );
}

function AgendaEmpty({
  title,
  message,
  bookingPath,
}: {
  title: string;
  message: string;
  bookingPath?: string;
}) {
  return (
    <Surface className="grid min-h-80 place-items-center px-6 text-center">
      <div className="max-w-md">
        <CalendarIcon className="mx-auto text-[var(--muted-foreground)]" size={30} />
        <h2 className="mt-4 text-lg font-black">{title}</h2>
        <p className="mt-2 text-sm leading-6 text-[var(--muted-foreground)]">{message}</p>
        {bookingPath && (
          <Button className="mt-5" asChild>
            <Link to={bookingPath}>Abrir agendamento público</Link>
          </Button>
        )}
      </div>
    </Surface>
  );
}

function AgendaSkeleton() {
  return (
    <div className="grid gap-4 sm:grid-cols-3" aria-label="Carregando agenda">
      <Skeleton variant="card" className="h-48" />
      <Skeleton variant="card" className="h-48" />
      <Skeleton variant="card" className="h-48" />
    </div>
  );
}
